import { message } from 'antd'
import { CancelToken } from 'axios'
import dayjs from 'dayjs'
import apiClient from 'enrollment-platform/api/enrollment-platform-client/api-client'
import { DateTimeFormats } from 'helpers/datetime'
import { BulkActionCensusFileRowsResponseDto } from './dto/bulk-action-census-file-rows.response.dto'
import { CensusFileRowUpdatesApproveDto } from './dto/census-file-member-updates-approve.dto'
import { CensusZendeskTicketJobDto } from './dto/census-zendesk-ticket-job.dto'
import { CensusFileRowZendeskDto } from './dto/census-file-member-updates-zendesk.dto'
import { CensusFileRowCountsDto } from './dto/census-file-row-counts.dto'
import { CensusFileRowTypeCountsRequestDto } from './dto/census-file-row-type-counts-request.dto'
import {
  CensusFieldIgnoreRuleDto,
  CreateCensusFieldIgnoreRuleRequestDto,
} from './dto/census-field-ignore-rule.dto'
import { CensusFileDto } from './dto/census-file.dto'
import { CensusFileRowDto } from './dto/census-file-row.dto'
import { CensusFileRowsRequestDto } from './dto/census-file-rows-request.dto'
import { CensusFileRowsResponseDto } from './dto/census-file-rows-response.dto'
import { CensusFileRowsSelectAllDto } from './dto/census-file-rows-select-all.dto'
import { CensusFilesRequestDto } from './dto/census-files-request.dto'
import { CensusFilesResponseDto } from './dto/census-files-response.dto'
import { CensusTemplateDownloadUrlDto } from './dto/census-template-download-url.dto'
import {
  CensusUploadQueueRequestDto,
  CensusUploadQueueResponseDto,
} from './dto/census-upload-job.dto'
import { CreateMembersResponseDto } from './dto/create-members-response.dto'
import {
  GenerateTestCensusRequestDto,
  GenerateTestCensusResponseDto,
} from './dto/generate-test-census.dto'
import { PresignedUploadUrlDto } from './dto/presigned-upload-url.dto'
import { UpdateCensusFileRowsResponseDto } from './dto/update-census-file-rows-response.dto'
import { CensusFileRowTypeEnum } from './enums/census-file-row-type.enum'
import { PatchActionEnum } from './enums/patch-action.enum'

const CONTENT_DISPOSITION_FILENAME_REGEX = /(?:^|;\s*)filename="?([^";]+)"?/i

export type CensusCsvDownloadType =
  | CensusFileRowTypeEnum
  | 'ACTIVITY'
  | 'IGNORED'

export type CensusReviewCsvDownloadType =
  | CensusFileRowTypeEnum.ERROR
  | CensusFileRowTypeEnum.UNDETERMINED
  | CensusFileRowTypeEnum.DRAFT_EMPLOYEE
  | CensusFileRowTypeEnum.UPDATE_EMPLOYEE_BENEFITS
  | CensusFileRowTypeEnum.UPDATE_EMPLOYEE_NO_BENEFITS
  | CensusFileRowTypeEnum.DUPLICATE_EMPLOYEE
  | 'IGNORED'
  | 'ACTIVITY'

export type CensusCsvRowDownloadType = CensusFileRowTypeEnum

export const createCensusFileFromS3 = async ({
  fileName,
  importPublicKey,
  clientPublicKey,
}: {
  fileName: string
  importPublicKey: string
  clientPublicKey: string
}) => {
  const ret = await apiClient.post('/census-files', {
    fileName,
    importPublicKey,
    clientPublicKey,
  })
  return ret.data
}

/**
 * [DEV ONLY] Generates a CMv2 census file with a target number of rows in each
 * Census Review bucket. The backend enforces both the Hixme platform-admin role
 * and the dev environment, returning 403 anywhere else.
 */
export const generateTestCensusFile = async (
  body: GenerateTestCensusRequestDto
): Promise<GenerateTestCensusResponseDto> => {
  const ret = await apiClient.post<GenerateTestCensusResponseDto>(
    'census-files/dev/generate-census',
    body
  )
  return ret.data
}

export const getCensusFiles = async (
  params?: CensusFilesRequestDto
): Promise<CensusFilesResponseDto> => {
  const url = '/census-files'
  const ret = await apiClient.get<CensusFilesResponseDto>(url, {
    params: params ?? {},
  })
  return ret.data
}

// Uploads that are queued, processing or failed (platform admins only)
export const getCensusUploadQueue = async (
  params?: CensusUploadQueueRequestDto
): Promise<CensusUploadQueueResponseDto> => {
  const ret = await apiClient.get<CensusUploadQueueResponseDto>(
    '/census-files/queue',
    { params: params ?? {} }
  )
  return ret.data
}

export const dismissCensusUploadJob = async (id: string): Promise<void> => {
  await apiClient.post(`/census-files/queue/${id}/dismiss`)
}

export const getCensusFileRows = async (
  params: CensusFileRowsRequestDto,
  options?: { cancelToken?: CancelToken }
): Promise<CensusFileRowsResponseDto> => {
  const { publicKey, ...other } = params
  const url = `census-files/${publicKey}/rows`
  const ret = await apiClient.get<CensusFileRowsResponseDto>(url, {
    params: other,
    cancelToken: options?.cancelToken,
  })
  return ret.data
}

export const getCensusFileRowTypeCounts = async (
  params: CensusFileRowTypeCountsRequestDto,
  options?: { cancelToken?: CancelToken }
): Promise<CensusFileRowCountsDto> => {
  const { publicKey, ...other } = params
  const url = `census-files/${publicKey}/rows/type-counts`
  const ret = await apiClient.get<CensusFileRowCountsDto>(url, {
    params: other,
    cancelToken: options?.cancelToken,
  })
  return ret.data
}

export const updateCensusFileRows = async (
  rowsOrSelectAll: CensusFileRowDto[] | CensusFileRowsSelectAllDto,
  publicKey: string,
  action?: PatchActionEnum,
  options?: { ignoreRules?: CreateCensusFieldIgnoreRuleRequestDto[] }
): Promise<
  UpdateCensusFileRowsResponseDto | { data: CreateMembersResponseDto }
> => {
  let url = `census-files/${publicKey}/rows`
  if (action) {
    url += `?action=${action}`
  }
  const shouldAttachIgnoreRules =
    action === PatchActionEnum.UPDATE_RECORDS && !!options?.ignoreRules?.length

  const payload =
    'selectAll' in rowsOrSelectAll
      ? rowsOrSelectAll
      : {
          params: rowsOrSelectAll,
          ...(shouldAttachIgnoreRules
            ? { ignoreRules: options?.ignoreRules }
            : {}),
        }
  const ret = await apiClient.patch(url, payload)
  return ret.data
}

export const setCensusFileHiddenFromHR = async (
  publicKey: string,
  hiddenFromHR: boolean
): Promise<CensusFileDto> => {
  const url = `census-files/${publicKey}/hidden-from-hr`
  const ret = await apiClient.patch<CensusFileDto>(url, { hiddenFromHR })
  return ret.data
}

export const bulkActionCensusFileRows = async (
  publicKey: string,
  action: PatchActionEnum
): Promise<BulkActionCensusFileRowsResponseDto> => {
  const url = `census-files/${publicKey}/rows/bulk-action`
  const ret = await apiClient.post(url, {
    action,
  })
  return ret.data
}

export const getPresignedUploadUrl = async ({
  clientId,
  fileName,
}: {
  clientId: string
  fileName: string
}): Promise<PresignedUploadUrlDto> => {
  const url = 'census-files/presigned-upload-url'
  const ret = await apiClient.post<PresignedUploadUrlDto>(url, {
    clientPublicKey: clientId,
    fileName,
  })
  return ret.data
}

export const getCensusTemplateDownloadUrl =
  async (): Promise<CensusTemplateDownloadUrlDto> => {
    const ret = await apiClient.get<CensusTemplateDownloadUrlDto>(
      'census-files/template/download-url'
    )
    return ret.data
  }

// upload file to s3
export const uploadCensusFile = async (presignedUrl: string, file) => {
  if (!file) {
    message.error('No file selected')
    return false
  }

  try {
    const response = await fetch(presignedUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': 'text/csv',
      },
    })

    if (response.ok) {
      return true
    } else {
      const errorText = await response.text()
      console.error('Upload failed:', response.status, errorText)
    }
  } catch (error: any) {
    message.error('Error uploading file: ', error?.message)
    console.error('Upload failed:', error.message)
  }
  return false
}

export const getFieldIgnoreRules = async (
  employeePublicKey: string
): Promise<CensusFieldIgnoreRuleDto[]> => {
  const ret = await apiClient.get<CensusFieldIgnoreRuleDto[]>(
    '/census-files/ignore-rules',
    {
      params: { employeePublicKey },
    }
  )
  return ret.data
}

export const deleteFieldIgnoreRule = async (
  id: number
): Promise<{ ok: boolean }> => {
  const ret = await apiClient.delete(`/census-files/ignore-rules/${id}`)
  return ret.data
}

/**
 * Lists the background Zendesk ticket-creation jobs for a census file, newest
 * first, including per-ticket results. Used by the Census Review "Background
 * Ticket Jobs" section, which polls this while any job is still running.
 */
export const getCensusZendeskTicketJobs = async (
  publicKey: string
): Promise<CensusZendeskTicketJobDto[]> => {
  const url = `census-files/${publicKey}/zendesk-ticket-jobs`
  const ret = await apiClient.get(url)
  return ret.data
}

export const approveCensusFileRowsUpdates = async (
  rows: CensusFileRowDto[],
  publicKey: string,
  options?: {
    ignoreRules?: CreateCensusFieldIgnoreRuleRequestDto[]
    unignoreRuleIds?: number[]
  }
): Promise<CensusFileRowUpdatesApproveDto> => {
  const url = `census-files/${publicKey}/approve`
  try {
    const ret = await apiClient.post(url, {
      params: rows,
      ...(options?.ignoreRules?.length
        ? { ignoreRules: options.ignoreRules }
        : {}),
      ...(options?.unignoreRuleIds?.length
        ? { unignoreRuleIds: options.unignoreRuleIds }
        : {}),
    })
    return ret.data
  } catch (error: any) {
    message.error('Error approving records: ', error?.message)
    console.error('Approve failed:', error.message)
    return {
      updatedMemberIds: [],
      errors: ['error approving members for updates'],
      rowsAffected: 0,
      failedRows: rows.length,
      zendeskTickets: [],
    }
  }
}

/**
 * Server-resolved bulk approve: hits the dedicated route that selects every
 * IN_REVIEW row in the file whose proposed diffs were all matched by ignore
 * rules. No client-side row payload — the backend uses the JSONB predicate.
 *
 * `expectedRowCount` is the caller's best estimate of how many rows the
 * backend will touch (typically `CensusFileRowCountsDto.fullyIgnored`). It is
 * only consulted in the error-fallback path so the caller can distinguish
 * failure from a legitimate zero-row response — without it, a thrown error
 * collapses to `failedRows: 0` and looks indistinguishable from success.
 */
export const approveFullyIgnoredCensusFileRows = async (
  publicKey: string,
  expectedRowCount: number
): Promise<CensusFileRowUpdatesApproveDto> => {
  const url = `census-files/${publicKey}/approve-fully-ignored`
  try {
    const ret = await apiClient.post(url)
    return ret.data
  } catch (error: any) {
    // Don't surface a toast here — the caller inspects `errors`/`failedRows`
    // and shows its own (more informative) message. Showing one here would
    // produce a duplicate toast on every failure.
    console.error('Approve fully-ignored failed:', error.message)
    return {
      updatedMemberIds: [],
      errors: ['error approving fully-ignored rows'],
      rowsAffected: 0,
      failedRows: Math.max(expectedRowCount, 1),
      zendeskTickets: [],
    }
  }
}

export const trackCensusFileRowsUpdates = async (
  rows: CensusFileRowDto[],
  publicKey: string
): Promise<CensusFileRowZendeskDto> => {
  const url = `census-files/${publicKey}/zendesk`
  try {
    const ret = await apiClient.post(url, {
      params: rows,
    })
    return ret.data
  } catch (error: any) {
    message.error('Error sending to zendesk', error?.message)
    console.error('Send to Zendesk failed:', error.message)
  }
  return {
    rowsAffected: 0,
    result: [],
  }
}

export const downloadCMv2RowReport = async (
  publicKey: string,
  rowType: CensusCsvRowDownloadType | 'ACTIVITY'
): Promise<{ blob: Blob; fileName: string }> => {
  const downloadType: CensusCsvDownloadType = rowType
  const url = `census-files/${publicKey}/download/${downloadType}`
  try {
    const ret = await apiClient.get<Blob>(url, {
      responseType: 'blob',
    })

    const timestamp = dayjs().format(DateTimeFormats.h)

    const contentDisposition = ret.headers['content-disposition']
    const filenameMatch = contentDisposition?.match(
      CONTENT_DISPOSITION_FILENAME_REGEX
    )
    const fileName =
      filenameMatch?.[1] ??
      `census-file-row-${publicKey}-${downloadType}-${timestamp}.csv`

    return {
      blob: ret.data,
      fileName,
    }
  } catch (error: any) {
    message.error('Error downloading error report: ', error?.message)
    console.error('Download error report failed:', error.message)
    throw error
  }
}

export const downloadCensusReviewCsvReport = async (
  publicKey: string,
  downloadType: CensusReviewCsvDownloadType
): Promise<{ blob: Blob; fileName: string }> => {
  const url = `census-files/${publicKey}/census-review/download/${downloadType}`
  const ret = await apiClient.get<Blob>(url, {
    responseType: 'blob',
  })

  const timestamp = dayjs().format(DateTimeFormats.h)
  const contentDisposition = ret.headers['content-disposition']
  const filenameMatch = contentDisposition?.match(
    CONTENT_DISPOSITION_FILENAME_REGEX
  )

  return {
    blob: ret.data,
    fileName:
      filenameMatch?.[1] ??
      `census-file-row-${publicKey}-${downloadType}-${timestamp}.csv`,
  }
}
